import {
  AspectRatio,
  Box,
  Button,
  Card,
  Flex,
  Grid,
  LinkBox,
  LinkOverlay,
  Text,
  TextProps,
  Tooltip,
} from '@chakra-ui/react';
import { Promos } from '@diamond/shared/types';
import { Icon, ShareButtonPopover } from '@diamond/shared/ui';
import { promoValidFromToFormat, trackEvent } from '@diamond/shared/utils';
import { TRACKING_EVENTS, TRACKING_PAGES } from '@diamond/sol-buyer/constant';
import { ShareOutlined as ShareIcon } from '@mui/icons-material';
import Link from 'next/link';

import { Image } from '../common';

interface PromoListProps {
  data: Promos['data'];
  section?: string;
}

interface TruncatedTextProps extends TextProps {
  children: React.ReactNode;
}

const TruncatedText: React.FC<TruncatedTextProps> = ({
  children,
  ...props
}) => (
  <Tooltip label={children} hasArrow>
    <Text
      overflow="hidden"
      textOverflow="ellipsis"
      whiteSpace="nowrap"
      {...props}
    >
      {children}
    </Text>
  </Tooltip>
);

export function PromoList({ data, section = '-' }: PromoListProps) {
  const baseUrl = window.location.origin;
  const promoWording =
    'Dapatkan produk unggulan dan nikmati penawaran terbatas, eksklusif untuk pengguna Sukanda Onelink!';

  const trackEventShare = (
    promo_id: string,
    promo_name: string | null,
    promo_brand: string,
    channel: string
  ) => {
    trackEvent(TRACKING_EVENTS.CLICK_PROMO_SHARE, {
      promo_id,
      promo_name,
      promo_brand,
      channel,
      section,
    });
  };

  return (
    <Grid
      templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}
      rowGap="6"
      columnGap="5"
    >
      {data.map((promo, index) => {
        const promoUrl = `${baseUrl}/promo/${promo.id}`;

        return (
          <LinkBox
            key={index}
            as={Card}
            overflow="hidden"
            rounded="16px"
            border={'1px solid #d9d9d9'}
            boxShadow="none"
            cursor="pointer"
            _hover={{
              boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
            }}
          >
            <Flex direction="column">
              <LinkOverlay
                as={Link}
                href={`/promo/${promo.id}`}
                onClick={() => {
                  trackEvent(TRACKING_EVENTS.CLICK_PROMO_DETAIL, {
                    section,
                    promo_id: promo.id,
                    promo_name: promo.mechanism,
                    promo_brand: promo.promo_name,
                    section_name: section,
                  });
                }}
              >
                <AspectRatio
                  overflow="hidden"
                  position="relative"
                  ratio={8 / 3}
                >
                  <Image
                    src={promo.banner_url || ''}
                    alt="Promo"
                    style={{ objectFit: 'cover' }}
                    fallbackSrc="/images/placeholder_promo.jpg"
                    fill
                  />
                </AspectRatio>
              </LinkOverlay>

              <Flex direction="column" p="4" gap="4">
                <Box>
                  <Text fontWeight="bold" fontSize="xs">
                    {promo.promo_name}
                  </Text>
                  {promo.mechanism && promo.mechanism.length > 52 ? (
                    <TruncatedText fontSize="md">
                      {promo.mechanism}
                    </TruncatedText>
                  ) : (
                    <Text fontSize="md">{promo.mechanism}</Text>
                  )}
                </Box>

                <Box>
                  <Text fontWeight="bold" fontSize="xs">
                    Periode Promo
                  </Text>
                  <Text fontSize="sm">
                    {promoValidFromToFormat(
                      promo.valid_from,
                      promo.valid_until,
                      false
                    )}
                  </Text>
                </Box>

                <Flex gap={2}>
                  <Button
                    as={Link}
                    href={`/promo/${promo.id}`}
                    w="full"
                    h="10"
                    variant="solid"
                    size="sm"
                    fontSize="sm"
                    rounded="8px"
                  >
                    Lihat Promo
                  </Button>

                  <ShareButtonPopover
                    title="Bagikan promo"
                    url={promoUrl}
                    textToShare={promoWording}
                    onWhatsappShare={() =>
                      trackEventShare(
                        promo.id,
                        promo.mechanism,
                        promo.promo_name,
                        'whatsapp'
                      )
                    }
                    onFacebookShare={() =>
                      trackEventShare(
                        promo.id,
                        promo.mechanism,
                        promo.promo_name,
                        'facebook'
                      )
                    }
                    onTelegramShare={() =>
                      trackEventShare(
                        promo.id,
                        promo.mechanism,
                        promo.promo_name,
                        'telegram'
                      )
                    }
                    onTwitterShare={() =>
                      trackEventShare(
                        promo.id,
                        promo.mechanism,
                        promo.promo_name,
                        'twitter'
                      )
                    }
                    onCopyShare={() =>
                      trackEvent(TRACKING_EVENTS.CLICK_COPY_PROMO, {
                        promo_id: promo.id,
                        promo_name: promo.mechanism,
                        promo_brand: promo.promo_name,
                        promo_code: promo.promo_code,
                        section_name: section,
                      })
                    }
                  >
                    <Button
                      w="10"
                      h="10"
                      variant="outline"
                      size="sm"
                      rounded="8px"
                    >
                      <Icon as={ShareIcon} color="black" />
                    </Button>
                  </ShareButtonPopover>
                </Flex>
              </Flex>
            </Flex>
          </LinkBox>
        );
      })}
    </Grid>
  );
}
